import { useAppDispatch } from '@/platform';
import { logout as logoutAction } from '@/shared/infra/store/authSlice';
import { useAppSelector } from '@/shared/infra/store/hooks';
import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { subscribeToIdentityChange } from './crossTabIdentity';

/**
 * Signs this tab out when another tab takes or drops the browser's session (VTK-1918 follow-up).
 *
 * One browser holds one session cookie, so the newest sign-in wins it for every tab. Without this,
 * the displaced tab carries on showing the old user and gating its UI on the old role while the
 * server executes its requests as the new one. Signing the stale tab out is the honest outcome:
 * the session it was using is genuinely gone.
 *
 * Mounted once, app-wide. It is inert for anyone not signed in, which is what keeps the public
 * candidate screens — Section 1, remote I-9, remediation — from ever being redirected to a staff
 * login by a staff member signing in elsewhere in the same browser.
 */
export function useDisplacedSessionGuard(): void {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const appUserId = useAppSelector((state) => state.auth.user?.appUserId);

  useEffect(() => {
    return subscribeToIdentityChange(appUserId, (announcedAppUserId) => {
      dispatch(logoutAction());
      // Say why, or being bounced to the login screen mid-task looks like a crash. Both routes here
      // end this tab's session, but they are different events to the person reading the toast: one
      // is somebody else taking the browser's cookie, the other is their own sign-out elsewhere.
      toast.info(
        announcedAppUserId
          ? 'You signed in as a different user in another tab, so this tab was signed out.'
          : 'You signed out in another tab, so this tab was signed out too.'
      );
      navigate('/login', { replace: true });
    });
  }, [appUserId, dispatch, navigate]);
}
